import {
  Checkbox,
  RadioButton,
  NumericTextBox
} from '@progress/kendo-react-inputs';
import cloneDeep from 'lodash.clonedeep';
import { useLocation } from 'react-router-dom';
import { Label } from '@progress/kendo-react-labels';
import { Col, Row, Tab, Tabs } from 'react-bootstrap';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { TaskEditor } from '.';
import PopupMenu, {
  popupMenu as MenuList
} from '../../../components/popupMenu';
import {
  UploadedImage,
  ResourceWindow
} from '../image-resource/resource-window';
import {
  TaskEntity,
  TimerEntity,
  ResourceEntity,
  TaskContentEntity
} from '../../../types/game-document/entities';
import TaskQrPdfExport from './task-qr-pdf-export';
import { Algorithm } from '../../../types/algorithm';
import CoverImage from '../../../components/cover-image';
import RequiredFields from '../../../types/required-fields';
import { GetResourceValue } from '../../../utils/game-document';
import { YesNoDialogResult } from '../../../types/dialog-result';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { NavigationSource } from '../../../types/navigation-source';
import { GameDocumentContext } from '../../../contexts/game-document';
import { TaskEvent } from '../../../types/game-document/entities/task';
import { useTaskCoordinates } from '../../../hooks/use-task-coordinates';
import EventAlgorithm from '../../../components/map/event-configuration/event--algorithm';

export interface TaskEditorWindowProps {
  navigationSource: NavigationSource;
  entity: TaskEntity;
  timer?: TimerEntity;
  requiredFields?: RequiredFields<TaskEntity>[];
  resources: ResourceEntity[];
  handleTaskChange?: (
    field: keyof TaskEntity,
    value:
      | boolean
      | string
      | TaskEvent
      | { enablePremessageOutsideRange?: boolean; setTaskProximity?: number }
      | undefined,
    name?: string
  ) => void;
  handleTaskResourceUpload?: (
    field: keyof TaskEntity,
    upload: UploadedImage
  ) => void;
  taskCoordinates?: { lat: number; lon: number };
  onCoordinateSave?: (lat: number, lon: number) => void;
}

const defaultAvailableImageUrl =
  'https://cdn.catalystglobal.games/resources/map-task.png';
const defaultCompleteImageUrl =
  'https://cdn.catalystglobal.games/resources/map-task--complete.png';

export default function TaskEditorFull({
  navigationSource,
  entity,
  requiredFields,
  resources,
  handleTaskChange = () => {},
  handleTaskResourceUpload = () => {},
  taskCoordinates,
  onCoordinateSave
}: TaskEditorWindowProps) {
  const { state: stateLocation } = useLocation();

  const [state] = useContext(GameDocumentContext);
  const [taskData, setTaskData] = useState<TaskEntity>({
    id: '',
    name: '',
    description: ''
  });
  const [iconUrl, setIconUrl] = useState<string>('');
  const [completeIconUrl, setCompleteIconUrl] = useState<string>('');
  const [dialogUploadVisible, setDialogUploadVisible] =
    useState<boolean>(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  const [fileUpload, setFileUpload] = useState<string>('');
  const [showDeleteIconConfirm, setShowDeleteIconConfirm] =
    useState<boolean>(false);
  const [showDeleteCompleteIconConfirm, setShowDeleteCompleteIconConfirm] =
    useState<boolean>(false);
  const [activeTab, setActiveTab] = useState('task-information');

  const taskMenu: MenuList[] = [
    { classIcon: 'edit', textMenu: 'Edit', textClass: '' },
    {
      classIcon: 'restore',
      textMenu: 'Restore to default',
      textClass: 'text-danger'
    }
  ];

  const inGameSettings = state.gameDocument?.settings.inGame;

  const isAssignedToGPSMap = useMemo(() => {
    if (!state.gameDocument) return false;
    let isAssignedToGPS = false;
    let mapAsId = '';

    state.gameDocument.rules.worldMap.tasks.forEach((t) => {
      if (t.taskAssId === entity.id)
        mapAsId = state.gameDocument?.rules.worldMap.mapAssId ?? '';
    });

    if (!mapAsId) {
      let zoneId = '';
      state.gameDocument.assets.zones?.forEach((z) => {
        z.tasks?.forEach((t) => {
          if (t.taskAssId === entity.id) zoneId = z.id;
        });
      });

      mapAsId =
        state.gameDocument.rules.worldMap.zones.find(
          (z) => z.zoneAssId === zoneId
        )?.mapAssId ?? '';
    }

    isAssignedToGPS =
      state.gameDocument.assets.maps?.find((m) => m.id === mapAsId)?.type !==
      'illustrated';

    return isAssignedToGPS;
  }, [entity.id, state.gameDocument]);

  const eventActionHandler = (type: keyof TaskEvent, algorithm: Algorithm) => {
    let newTask: TaskEntity = cloneDeep({ ...taskData });
    if (newTask.events === undefined) {
      newTask.events = {};
    }
    newTask.events[type] = algorithm;
    handleTaskChange('events', newTask.events);
  };

  const handleEventOnOpenChange = (algorithm: Algorithm) => {
    eventActionHandler('onOpenTask', algorithm);
  };

  const handleEventOnCompleteChange = (algorithm: Algorithm) => {
    eventActionHandler('onCompleteTask', algorithm);
  };

  const handleEventOnCloseChange = (algorithm: Algorithm) => {
    eventActionHandler('onCloseTask', algorithm);
  };

  const handleEventOnProximityChange = (algorithm: Algorithm) => {
    eventActionHandler('onProximity', algorithm);
  };

  const handleEditorSubmit = (
    uploadedFile: UploadedImage,
    fileUpload: string
  ) => {
    setDialogUploadVisible(false);
    handleTaskResourceUpload(fileUpload as keyof TaskEntity, uploadedFile);
  };

  const handleTaskIconSelected =
    (field: string) => (id: number, menu: MenuList) => {
      if (field === 'availableImage' && menu.textMenu === 'Edit') {
        setDialogUploadVisible(true);
        setFileUpload('imageResId');
        setSelectedImageUrl(
          iconUrl === '' ? defaultAvailableImageUrl : iconUrl
        );
      } else if (field === 'completeImage' && menu.textMenu === 'Edit') {
        setDialogUploadVisible(true);
        setFileUpload('completeImageResId');
        setSelectedImageUrl(
          iconUrl === '' ? defaultCompleteImageUrl : completeIconUrl
        );
      } else if (
        field === 'availableImage' &&
        menu.textMenu === 'Restore to default'
      ) {
        setShowDeleteIconConfirm(true);
      } else if (
        field === 'completeImage' &&
        menu.textMenu === 'Restore to default'
      ) {
        setShowDeleteCompleteIconConfirm(true);
      }
    };

  const onConfirmDeleteIcon = (result: YesNoDialogResult) => {
    if (result === 'yes') {
      const defaultTaskAvailableIconResId =
        state.gameDocument?.settings.designer?.defaultTaskAvailableIconResId ||
        '';
      handleTaskChange('imageResId', defaultTaskAvailableIconResId);
    }

    setShowDeleteIconConfirm(false);
  };

  const onConfirmDeleteCompleteIcon = (result: YesNoDialogResult) => {
    if (result === 'yes') {
      const defaultTaskCompletedIconResId =
        state.gameDocument?.settings.designer?.defaultTaskCompletedIconResId ||
        '';
      handleTaskChange('completeImageResId', defaultTaskCompletedIconResId);
    }

    setShowDeleteCompleteIconConfirm(false);
  };

  const toggleUploadDialog = () => setDialogUploadVisible(!dialogUploadVisible);

  useEffect(() => {
    if (entity) {
      setTaskData(entity);
      setCompleteIconUrl(
        resources?.find((x) => x?.id === entity?.completeImageResId)?.value ??
          ''
      );

      setIconUrl(
        resources?.find((x) => x?.id === entity?.imageResId)?.value ?? ''
      );
    }
  }, [entity, resources]);

  useEffect(() => {
    let gameDocTaskContents = [];
    gameDocTaskContents = state.gameDocument?.assets?.taskContents!;
    const titleValue = GetResourceValue(
      state.gameDocument!,
      entity.titleResId ?? ''
    );
    let newTaskContent: TaskContentEntity = {
      id: '-',
      bodyType: 'HTML',
      forms: [],
      contentResId: '',
      preMessageResId: '',
      title: `${titleValue} - task content`,
      name: `Create new`,
      description: `${titleValue} - task content`
    };

    let taskContentIndex = gameDocTaskContents.findIndex((x) => x.id === '-');
    if (taskContentIndex === -1) {
      gameDocTaskContents.unshift(newTaskContent);
    }
  }, []);

  const imageUrl = useCallback(
    (res: 'imageResId' | 'completeImageResId') => {
      if (
        state.gameDocument?.settings &&
        state.gameDocument?.settings.designer
      ) {
        const availableTaskIcon = GetResourceValue(
          state.gameDocument,
          state.gameDocument.settings.designer.defaultTaskAvailableIconResId
        );
        const completeTaskIcon = GetResourceValue(
          state.gameDocument,
          state.gameDocument.settings.designer.defaultTaskCompletedIconResId
        );
        return res === 'imageResId' ? availableTaskIcon : completeTaskIcon;
      } else {
        return res === 'imageResId'
          ? defaultAvailableImageUrl
          : defaultCompleteImageUrl;
      }
    },
    [state.gameDocument?.settings]
  );

  const {
    coords,
    errors,
    combinedCoords,
    handleLatChange,
    handleLonChange,
    handleCombinedChange,
    getValidCoords,
    hasErrors
  } = useTaskCoordinates(taskCoordinates?.lat, taskCoordinates?.lon);

  useEffect(() => {
    if (!stateLocation?.openTab) return;

    setActiveTab(stateLocation?.openTab);
  }, [stateLocation?.openTab]);

  return (
    <>
      <Row className={'mt-3'}>
        <Tabs
          unmountOnExit
          activeKey={activeTab}
          onSelect={(tab) => setActiveTab(tab ?? 'task-information')}
          id={'task-tabs'}
          className={'mb-3'}>
          <Tab eventKey={'task-information'} title={'Information'}>
            <div className={'d-flex flex-row gap-4'}>
              <div
                className={'task-icon d-flex flex-column gap-2 flex-shrink-0'}>
                <Label>Available Icon</Label>
                <CoverImage
                  containerStyle={'square'}
                  imageUrl={iconUrl === '' ? imageUrl('imageResId') : iconUrl}>
                  <PopupMenu
                    id={0}
                    menus={taskMenu}
                    onMenuSelected={handleTaskIconSelected('availableImage')}
                  />
                </CoverImage>
                <Checkbox
                  id={'complete-icon'}
                  name={'checkbox-use-default'}
                  value={!Boolean(entity.hideWhenComplete)}
                  onChange={(e) =>
                    handleTaskChange(
                      'hideWhenComplete',
                      !e.target.value as boolean
                    )
                  }>
                  <label
                    className={'k-checkbox-label '}
                    style={{
                      fontWeight: 600
                    }}
                    htmlFor={'complete-icon'}>
                    Complete Icon
                  </label>
                </Checkbox>
                {!Boolean(entity.hideWhenComplete) && (
                  <CoverImage
                    containerStyle={'square'}
                    imageUrl={
                      completeIconUrl === ''
                        ? imageUrl('completeImageResId')
                        : completeIconUrl
                    }>
                    <PopupMenu
                      id={0}
                      menus={taskMenu}
                      onMenuSelected={handleTaskIconSelected('completeImage')}
                    />
                  </CoverImage>
                )}
              </div>
              <div
                className={
                  'task-information d-flex flex-column flex-grow-1 gap-2'
                }>
                <TaskEditor
                  editorMode={'basic'}
                  navigationSource={navigationSource}
                  entity={entity!}
                  displayTimer
                  displayTaskTitleSetting
                  resources={resources}
                  handleTaskChange={(field, value) => {
                    handleTaskChange(field, value as string);
                  }}
                  requiredFields={requiredFields}
                />

                <div className={'d-flex gap-2 mt-2'}>
                  <span className={'fw-bold'}>QR Code task</span>
                  <Checkbox
                    size={'large'}
                    name={'allowGameAutomaticStart'}
                    defaultChecked={entity.enableQRCode}
                    onChange={(e) => handleTaskChange('enableQRCode', e.value)}
                  />
                </div>

                {entity.enableQRCode && (
                  <TaskQrPdfExport
                    {...(state.gameDocument?.overview?.logoResId && {
                      logoUrl: GetResourceValue(
                        state.gameDocument,
                        state.gameDocument?.overview?.logoResId
                      )
                    })}
                    qrValue={`${entity.id}`}
                    taskTitle={GetResourceValue(
                      state.gameDocument!,
                      entity.titleResId ?? ''
                    )}
                    gameTitle={GetResourceValue(
                      state.gameDocument!,
                      state.gameDocument?.overview?.titleResId ?? ''
                    )}
                    fileName={
                      'QR - ' +
                      GetResourceValue(
                        state.gameDocument!,
                        entity.titleResId ?? ''
                      )
                    }
                  />
                )}

                {/* proximity setting override only displayed for task that assigned to GPS and has coordinates */}
                {isAssignedToGPSMap && taskCoordinates && (
                  <>
                    <div className={'d-flex flex-column gap-2 mt-3'}>
                      <span className={'fw-bold'}>Default Game Settings</span>
                    </div>
                    <Row>
                      <Col>
                        Task require Proximity:{' '}
                        {inGameSettings?.tasksRequireProximity ? 'On' : 'Off'}
                      </Col>
                      <Col>
                        Task Proximity: {inGameSettings?.setTaskProximity}{' '}
                        meters
                      </Col>
                      <Col>
                        Pre Message:{' '}
                        {inGameSettings?.enablePremessageOutsideRange
                          ? 'On'
                          : 'Off'}
                      </Col>
                    </Row>

                    <div className={'d-flex gap-2 mt-2'}>
                      <span className={'fw-bold'}>Override Game Settings</span>
                      <Checkbox
                        name={'allowGameAutomaticStart'}
                        size={'large'}
                        onChange={(e) =>
                          handleTaskChange(
                            'settingsOverride',
                            e.value
                              ? {
                                  setTaskProximity:
                                    inGameSettings?.setTaskProximity ?? 0,
                                  enablePremessageOutsideRange:
                                    inGameSettings?.enablePremessageOutsideRange
                                }
                              : undefined
                          )
                        }
                        defaultChecked={entity.settingsOverride !== undefined}
                      />
                    </div>

                    {entity.settingsOverride !== undefined && (
                      <>
                        <div>
                          <div
                            className={
                              'd-flex gap-1 align-items-center mt-2 w-100'
                            }>
                            <span className={'mr-10 w-30'}>
                              Set task proximity
                            </span>
                            <NumericTextBox
                              name={'setTaskProximity'}
                              className={'mr-1 w-10'}
                              value={
                                entity.settingsOverride.setTaskProximity ?? 0
                              }
                              onChange={(e) => {
                                const settingsOverride = cloneDeep(
                                  entity.settingsOverride
                                );
                                if (settingsOverride)
                                  settingsOverride.setTaskProximity =
                                    e.value ?? 0;

                                handleTaskChange(
                                  'settingsOverride',
                                  settingsOverride
                                );
                              }}
                              min={0}
                            />
                            <span>Meters</span>
                          </div>

                          <div
                            className={'d-flex gap-1 align-items-center mb-2'}>
                            <span className={'mb-2 fs-6 text-black-50'}>
                              <span
                                className={'material-symbols-outlined fs-6'}>
                                lightbulb
                              </span>
                              Set the proximity required before a task becomes
                              available.
                            </span>
                          </div>
                        </div>

                        <div className={'d-flex gap-1 align-items-center'}>
                          <span className={'mr-10 w-30'}>Pre-message text</span>
                          <RadioButton
                            name={'enablePremessageOutsideRange'}
                            value={true}
                            label={'On'}
                            checked={
                              entity.settingsOverride
                                ?.enablePremessageOutsideRange
                            }
                            onChange={(e) => {
                              const settingsOverride = cloneDeep(
                                entity.settingsOverride
                              );
                              if (settingsOverride)
                                settingsOverride.enablePremessageOutsideRange = true;

                              handleTaskChange(
                                'settingsOverride',
                                settingsOverride
                              );
                            }}
                          />

                          <RadioButton
                            name={'enablePremessageOutsideRange'}
                            value={false}
                            label={'Off'}
                            className={'ml-4'}
                            checked={
                              !entity.settingsOverride
                                ?.enablePremessageOutsideRange
                            }
                            onChange={(e) => {
                              const settingsOverride = cloneDeep(
                                entity.settingsOverride
                              );
                              if (settingsOverride)
                                settingsOverride.enablePremessageOutsideRange = false;

                              handleTaskChange(
                                'settingsOverride',
                                settingsOverride
                              );
                            }}
                          />
                        </div>

                        <div className={'d-flex gap-1 align-items-center'}>
                          <span className={'mb-2 fs-6 text-black-50'}>
                            <span className={'material-symbols-outlined fs-6'}>
                              lightbulb
                            </span>
                            Enable pre-message text when not in range.
                          </span>
                        </div>
                      </>
                    )}
                  </>
                )}

                {taskCoordinates && (
                  <div className={'d-flex flex-column gap-2 mt-3'}>
                    <label className={'fw-bold'}>Coordinates</label>
                    <div className={'d-flex flex-column gap-1'}>
                      <label>Combined (lat, lon)</label>
                      <input
                        className={'form-control'}
                        type={'text'}
                        value={combinedCoords}
                        placeholder={'e.g. -37.8136, 144.9631'}
                        onChange={(e) => handleCombinedChange(e.target.value)}
                      />
                    </div>
                    <div className={'d-flex gap-2'}>
                      <div className={'d-flex flex-column flex-fill'}>
                        <label>Latitude</label>
                        <input
                          className={'form-control'}
                          type={'text'}
                          value={coords.lat}
                          placeholder={'e.g. -37.8136'}
                          onChange={(e) => handleLatChange(e.target.value)}
                        />
                        {errors.lat && (
                          <small className={'text-danger'}>{errors.lat}</small>
                        )}
                      </div>
                      <div className={'d-flex flex-column flex-fill'}>
                        <label>Longitude</label>
                        <input
                          className={'form-control'}
                          type={'text'}
                          value={coords.lon}
                          placeholder={'e.g. 144.9631'}
                          onChange={(e) => handleLonChange(e.target.value)}
                        />
                        {errors.lon && (
                          <small className={'text-danger'}>{errors.lon}</small>
                        )}
                      </div>
                    </div>
                    <button
                      type={'button'}
                      className={
                        'btn btn-outline-primary btn-sm align-self-start'
                      }
                      disabled={hasErrors}
                      onClick={() => {
                        const valid = getValidCoords();
                        if (valid) onCoordinateSave?.(valid[0], valid[1]);
                      }}>
                      Update position
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Tab>

          <Tab eventKey={'task-onopen'} title={'Opened'}>
            <EventAlgorithm
              action={'Task.onOpen'}
              shortcutText={'What happens when a player opens this task?'}
              taskId={entity.id}
              description={'This runs when a player opens this task.'}
              onChange={handleEventOnOpenChange}
              algorithm={entity.events?.onOpenTask}
            />
          </Tab>

          <Tab eventKey={'task-oncomplete'} title={'Completed'}>
            <EventAlgorithm
              action={'Task.onComplete(score)'}
              shortcutText={'What happens when a player completes this task?'}
              taskId={entity.id}
              description={'This runs when a player completes this task.'}
              onChange={handleEventOnCompleteChange}
              algorithm={entity.events?.onCompleteTask}
            />
          </Tab>

          <Tab eventKey={'task-onclose'} title={'Closed'}>
            <EventAlgorithm
              action={'Task.onClose'}
              shortcutText={'What happens when a player closes this task?'}
              taskId={entity.id}
              description={'This runs when a player closes this task.'}
              onChange={handleEventOnCloseChange}
              algorithm={entity.events?.onCloseTask}
            />
          </Tab>

          <Tab eventKey={'task-onproximity'} title={'Within range'}>
            <EventAlgorithm
              action={'Task.onProximity'}
              shortcutText={
                'What happens when a player enters the range of this task?'
              }
              taskId={entity.id}
              description={
                'This runs when a player enters the range of this task.'
              }
              onChange={handleEventOnProximityChange}
              algorithm={entity.events?.onProximity}
            />
          </Tab>
        </Tabs>
      </Row>

      {dialogUploadVisible && (
        <ResourceWindow
          toggleDialog={toggleUploadDialog}
          onSubmit={(uploadedFile) => {
            handleEditorSubmit(uploadedFile, fileUpload);
          }}
          fileType={['image']}
          imageUrl={selectedImageUrl}
          defaultSelectedCategory={'Task icons'}
        />
      )}

      {showDeleteIconConfirm && (
        <YesNoDialog
          title={'Confirm restore'}
          onConfirm={onConfirmDeleteIcon}
          onClose={() => setShowDeleteIconConfirm(false)}>
          Are you sure you want to restore the task icon to default?
        </YesNoDialog>
      )}

      {showDeleteCompleteIconConfirm && (
        <YesNoDialog
          title={'Confirm restore'}
          onConfirm={onConfirmDeleteCompleteIcon}
          onClose={() => setShowDeleteCompleteIconConfirm(false)}>
          Are you sure you want to restore the task icon to default?
        </YesNoDialog>
      )}
    </>
  );
}
